"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import {
  agentService,
  type FunctionConfig,
} from "../../../services/agentService.ts";
import { AgentFunctions } from "./AgentFunctions.tsx";

interface AgentFunctionsWrapperProps {
  agentId: string;
  llmId: string;
  initialFunctions: FunctionConfig[];
}

export const AgentFunctionsWrapper: React.FC<AgentFunctionsWrapperProps> = ({
  agentId,
  llmId,
  initialFunctions,
}) => {
  const [functions, setFunctions] =
    useState<FunctionConfig[]>(initialFunctions);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const fetchFunctions = useCallback(async () => {
    setIsLoading(true);
    try {
      const llmData = await agentService.getRetellLLM(llmId);
      if (llmData?.general_tools) {
        setFunctions(llmData.general_tools);
      }
    } catch (error) {
      console.error("Error fetching functions:", error);
      toast({
        title: "Error fetching functions",
        status: "error",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [llmId, toast]);

  useEffect(() => {
    fetchFunctions();
  }, [fetchFunctions]);

  const handleUpdate = useCallback(
    async (updatedFunctions: FunctionConfig[]) => {
      setIsLoading(true);
      try {
        await agentService.updateRetellLLM(llmId, {
          agent_id: agentId,
          general_tools: updatedFunctions,
        });
        setFunctions(updatedFunctions);
        toast({
          title: "Functions updated successfully",
          status: "success",
          duration: 2000,
        });
      } catch (error) {
        console.error("Error updating functions:", error);
        toast({
          title: "Error updating functions",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [llmId, agentId, toast]
  );

  return (
    <AgentFunctions
      agentId={agentId}
      llmId={llmId}
      functions={functions}
      onUpdate={handleUpdate}
      isLoading={isLoading}
    />
  );
};
